import { useEffect, useMemo, useRef, useState } from "react";
import { DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Field, Form, FormElement } from "@progress/kendo-react-form";
import { useHistory } from "react-router-dom";

import { Button } from "components/Button/Button";
import { Dialog } from "components/Dialog/Dialog";
import { Icon, IconType } from "components/Icon/Icon";
import { LoadingPanel } from "components/Loading/LoadingPanel";
import { MultiColumnComboBoxField } from "components/forms/MultiColumnComboBoxField";
import { TextField } from "components/forms/TextField";
import { useNewEncounter } from "contexts/NewEncounterContext";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { DomainType, getAvailableDomainEntities } from "models";
import { REGEX_ALPHANUMERIC_PATTERN } from "utils/input";

import { DiscardChangesDialog } from "../DiscardChangesDialog";

import { DomainRadioGroupField } from "./DomainRadioGroupField";
import { COLUMNS, formValidator, mapDisplayValue } from "./helpers";

import "../Dialogs.scss";

import type { FormRenderProps } from "@progress/kendo-react-form";
import type { IdDescriptionBase } from "models/patientEncounter";
import type { ComboboxIdDescriptionBase, NewEncounterForm } from "./helpers";

export const NewEncounterDialog = () => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  const dialogContainerRef = useRef<HTMLDivElement>(null);
  const history = useHistory();

  const { hasFAC, hasPSA, loading: featuresLoading } = useFeatureFlags();

  const [selectedDomain, setSelectedDomain] = useState("");

  useEffect(() => {
    if (!featuresLoading && !selectedDomain) {
      const defaultDomain =
        hasFAC ? DomainType.Facility
        : hasPSA ? DomainType.Practice
        : "";

      setSelectedDomain(defaultDomain);
    }
  }, [featuresLoading, hasFAC, hasPSA, selectedDomain]);

  const {
    showNewEncounterDialog,
    closeNewEncounterDialog,
    handleCreateNewEncounter,
    buildNewEncounterChoiceLists,
    getFacilitiesChoiceList,
    getPracticeChoiceList,
    choiceLists,
    encounterOpened,
  } = useNewEncounter();

  useEffect(() => {
    if (!showNewEncounterDialog) return;
    if (hasFAC) {
      getFacilitiesChoiceList();
    }
    if (hasPSA) {
      getPracticeChoiceList();
    }
  }, [showNewEncounterDialog, hasFAC, hasPSA, getFacilitiesChoiceList, getPracticeChoiceList]);

  const handleClose = () => {
    setShowConfirmation(true);
  };

  const handleConfirm = () => {
    setShowConfirmation(false);
    closeNewEncounterDialog();
  };

  const facilities = useMemo(
    () => (hasFAC ? getAvailableDomainEntities(choiceLists.facilities).map(mapDisplayValue) : []),
    [hasFAC, choiceLists],
  );

  const practices = useMemo(
    () => (hasPSA ? getAvailableDomainEntities(choiceLists.practices).map(mapDisplayValue) : []),
    [hasPSA, choiceLists],
  );

  const handleSubmit = (formData: NewEncounterForm) => {
    handleCreateNewEncounter(
      choiceLists,
      formData.medicalRecordNumber,
      formData.facility?.ViewId,
      formData.type?.ViewId,
      formData.accountNumber,
    );

    if (encounterOpened) {
      const params = new URLSearchParams({
        encounterType: formData.type?.ViewId ?? "",
        accountNumber: formData.accountNumber ?? "",
      });

      window.open(
        `${process.env.PUBLIC_URL}/create/${formData.medicalRecordNumber}/${formData.facility?.ViewId}?${params}`,
      );
    } else {
      history.push("/encounter/create", {
        newEncounter: true,
        mrn: formData.medicalRecordNumber,
        facility: formData.facility?.ViewId,
        encounterType: formData.type?.ViewId ?? null,
        accountNumber: formData.accountNumber ?? null,
      });
    }

    closeNewEncounterDialog();
  };

  const encounterTypesList = useMemo(() => {
    if (!choiceLists.newEncounterEncounterTypes.length) {
      return [];
    }
    return choiceLists.newEncounterEncounterTypes.map(mapDisplayValue);
  }, [choiceLists.newEncounterEncounterTypes]);

  if (!showNewEncounterDialog) {
    return null;
  }

  if (!featuresLoading && !hasPSA && !hasFAC) {
    return (
      <div ref={dialogContainerRef}>
        <Dialog
          appendTo={dialogContainerRef.current}
          className="new-encounter-dialog"
          onClose={closeNewEncounterDialog}
          title="New Patient Encounter"
        >
          <p className="k-mb-0">You don't have permission to create new encounters.</p>
          <DialogActionsBar layout="end">
            <Button
              type="button"
              data-testid="cancel-create-button"
              onClick={closeNewEncounterDialog}
            >
              Ok
            </Button>
          </DialogActionsBar>
        </Dialog>
      </div>
    );
  }

  return (
    <Form
      key={selectedDomain}
      initialValues={{
        medicalRecordNumber: "",
        accountNumber: "",
        facility: null,
        type: null,
        domain: selectedDomain,
      }}
      validator={formValidator}
      onSubmit={handleSubmit}
      render={(formRenderProps: FormRenderProps) => {
        const showPSAForm =
          hasPSA && hasFAC ? formRenderProps.valueGetter("domain") === DomainType.Practice : hasPSA;

        const handleChangeFacility = () => {
          const selectedValue = formRenderProps.valueGetter("facility") as IdDescriptionBase | null;

          if (formRenderProps.valueGetter("type")) {
            formRenderProps.onChange("type", { value: null });
          }

          if (selectedValue?.ViewId) {
            buildNewEncounterChoiceLists(choiceLists, selectedValue.ViewId);
          }
        };

        const handleDomainChange = () => {
          const domain = formRenderProps.valueGetter("domain") as string;
          setSelectedDomain(domain);
        };

        return (
          <div ref={dialogContainerRef}>
            <Dialog
              appendTo={dialogContainerRef.current}
              className="new-encounter-dialog"
              onClose={handleClose}
              title="New Patient Encounter"
            >
              {featuresLoading && <LoadingPanel />}
              <FormElement>
                {selectedDomain && hasFAC && hasPSA && (
                  <Field
                    name="domain"
                    component={DomainRadioGroupField}
                    onChange={handleDomainChange}
                  />
                )}

                <Field
                  id="pe-MRN-Create"
                  name="medicalRecordNumber"
                  label="MRN"
                  component={TextField}
                  placeholder="Enter patient's MRN"
                  maxLength={24}
                  pattern={REGEX_ALPHANUMERIC_PATTERN}
                  testId="create-encounter-mrn"
                />
                {showPSAForm && (
                  <Field
                    name="accountNumber"
                    label="Account Number"
                    component={TextField}
                    placeholder="Account Number"
                    testId="create-encounter-accountNumber"
                    maxLength={24}
                  />
                )}
                <Field
                  component={MultiColumnComboBoxField<ComboboxIdDescriptionBase>}
                  name="facility"
                  label={!showPSAForm ? "Facility" : "Practice"}
                  data={!showPSAForm ? facilities : practices}
                  dataItemKey="ViewId"
                  textField="displayValue"
                  onChange={!showPSAForm ? handleChangeFacility : undefined}
                  testId="create-encounter-facility"
                  columns={COLUMNS}
                  filterable
                  controlConfig={{
                    loading: !!choiceLists.loadingNewEncounterFacilities,
                    onOpen: showPSAForm ? getPracticeChoiceList : getFacilitiesChoiceList,
                    placeholder: showPSAForm ? "Select Practice" : "Select Facility",
                  }}
                />

                {!showPSAForm && (
                  <Field
                    component={MultiColumnComboBoxField<ComboboxIdDescriptionBase>}
                    name="type"
                    label="Encounter Type"
                    testId="create-encounter-type"
                    data={encounterTypesList}
                    dataItemKey="ViewId"
                    textField="displayValue"
                    columns={COLUMNS}
                    disabled={
                      choiceLists.newEncounterEncounterTypes.length === 0 ||
                      formRenderProps.valueGetter("facility") === null
                    }
                    controlConfig={{
                      loading: !!choiceLists.loadingNewEncounterEncounterTypes,
                      placeholder: "Select Encounter Type",
                    }}
                  />
                )}

                <DialogActionsBar layout="end">
                  <Button type="button" data-testid="cancel-create-button" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    themeColor="primary"
                    id="create-encounter-link"
                    data-testid="create-encounter-link"
                    disabled={!formRenderProps.allowSubmit || !formRenderProps.valid}
                  >
                    Add Encounter
                    <Icon type={IconType.ExternalLink} />
                  </Button>
                </DialogActionsBar>
              </FormElement>

              {showConfirmation && (
                <DiscardChangesDialog
                  onCancel={() => {
                    setShowConfirmation(false);
                  }}
                  onDiscard={handleConfirm}
                />
              )}
            </Dialog>
          </div>
        );
      }}
    />
  );
};
